import * as _ from 'lodash';
import { NavigationExtras, Params, Router } from '@angular/router';
import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';

import { IPageData, PageData } from './models/page-data';
import { IRouteKeyData, routeKeyData } from '../route-service/models/route-key-data';
import { LoadingPageService } from '../loading-services/loading-page.service';
import { LogService } from '../log.service';
import { RouteActionParam, routeActionParam } from './models/return-page-query-param';
import { ActivatedRouteService } from '../route-service/activated-route.service';
import { HeaderData, IHeaderData } from 'src/app/features/shared/header/models/header-data';
import { FooterData, IFooterData } from 'src/app/features/shared/footer/models/footer-data';
import { appConfig } from 'src/config';
import { routePage, TRoutePage } from '../route-service/models/route-page';
import { NavigationStackService } from 'src/app/features/shared/navigation-stack/services/navigation-stack.service';
import { UserStorageService } from 'src/app/features/shared/storage/services/storage-services/user-storage-service';
import { TRouteName } from '../route-service/models/route-name';
import { TRouteCreate } from '../route-service/models/route-create';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

const homePage = appConfig.page_home;

export interface IPageService {
    pageCurrent$$: Signal<IPageData | null>;
    pageHome$$: Signal<IPageData | null>;
    pageReturn$$: Signal<IPageData | null>;
    pageStart$$: Signal<boolean | null>;
}


@Injectable()
export abstract class _PageService
    implements IPageService
{
    abstract pageCreate_goto(routeParams?: {[key: string]: string}): void;
    abstract pageRoute_goto(item: any, keep?: RouteActionParam, routeParams?: {[key: string]: string}): void;

    protected readonly _router = injectRequired(Router);
    protected readonly _activatedRouteService = inject(ActivatedRouteService, {optional: true});
    protected readonly _navigationStackService = injectRequired(NavigationStackService);
    protected readonly _userStorageService = inject(UserStorageService, {optional: true});
    protected readonly _loadingPageService = injectRequired(LoadingPageService);

    private __footerData_page$$ = signal<Partial<IFooterData>>(new FooterData());
    footerData_page$$ = this.__footerData_page$$.asReadonly();

    private __headerData_page$$ = signal<Partial<IHeaderData>>(HeaderData.create({}) as IHeaderData);
    headerData_page$$ = this.__headerData_page$$.asReadonly();

    private __pageCreate_name$$ = signal<TRouteName | null>(null);
    pageCreate_name$$ = this.__pageCreate_name$$.asReadonly();
    get PageCreate_name() {return this.__pageCreate_name$$();}

    pageCurrent_name$$ = computed<TRouteName | null>(() => this.pageCurrent$$()?.routePage ?? null);
    get PageCurrent_name() {return this.pageCurrent_name$$();}
    
    private __pageHome$$ = signal<IPageData | null>(null);
    pageHome$$ = this.__pageHome$$.asReadonly();
    get PageHome() {return this.__pageHome$$();}

    private __pageStart$$ = signal<boolean | null>(null);
    pageStart$$ = this.__pageStart$$.asReadonly();
    get PageStart() {return this.__pageStart$$();}
    set PageStart(value) {
        this.__pageStart$$.set(value);
    }

    private __pageCurrent$$ = signal<IPageData | null>(null);
    pageCurrent$$ = this.__pageCurrent$$.asReadonly();
    get PageCurrent() {return this.pageCurrent$$();}
    
    // De terugbestemming is niet langer een per-pagina ingestelde waarde, maar het frame ónder het
    // huidige in de navigatiestack. Daarmee volgt de terugknop de werkelijk afgelegde weg, en valt
    // hij bij een deep link terug op de gesynthetiseerde hiërarchische ouder.
    pageReturn$$ = computed<IPageData | null>(() => this._navigationStackService.framePrevious$$());
    get PageReturn(): IPageData | null {return this.pageReturn$$();}

    // Geen pageCurrent_name-parameter (meer): dat was een statische, per-serviceklasse waarde die
    // fout ging zodra een _PageService-instantie werd hergebruikt over meerdere componenten (bv.
    // AccountPageService voor zowel Home als Login) — de constructor kon dan maar één vaste pagina
    // kennen. pageCurrent$$ wordt nu uitsluitend gezet via pageCurrent_set(), aangeroepen door het
    // aanroepende route-component zelf (zie _BaseRouteComponent._ngOnInit), dat de enige
    // authoritatieve bron is voor "welke pagina is dit nu".
    constructor(
        protected __routeCreate: TRouteCreate | null,
    ) {
        this.__pageHome$$.set(this.pageData_get(homePage));
        this.__pageCreate_name$$.set(this.__routeCreate);

        // Persisteert de huidige pagina als user-brede startpagina in localStorage (user-storage),
        // zodat login later weet waar naartoe te navigeren (zie AccountPageService.login$ en
        // LoginDetailsComponent.__startPage_url). Gegated op hydrated$$: start-pages (bv. Domain)
        // renderen vaak vóórdat het auth-token verwerkt is (zie TabListService voor hetzelfde
        // probleem bij tab-restore) — schrijven vóór hydratie wordt stilzwijgend overschreven zodra
        // de user-scoped storage-tree daarna alsnog laadt.
        effect(() => {
            if (!this._userStorageService?.hydrated$$()) return;

            const __shouldPersist = this.__pageStart$$();
            const __currentPage = this.pageCurrent$$();

            if (__shouldPersist && __currentPage?.routePath) {
                untracked(() => this._userStorageService?.pageStart$$.set(__currentPage));
            }
        });
    }

    protected _createPage_goto(routeParams?: {[key: string]: string}): void {
        // page_goto dispatches on the runtime value of value1: PageCreate_name is always a
        // real routePage entry, so this always takes the TRoutePage-branch (value2=routeKeys,
        // value3=routeActions, value4=routeParams) regardless of the (loosely-typed) TRouteName
        // signature above — routeParams must go in position 4, not 3, or it gets misread as
        // routeActions and crashes on `.includes()` when routeParams is actually passed (e.g. isTab).
        //
        // routeKeys was hardcoded {} hier — voor een pageloos create-doel (bv. domainCreate) onschuldig,
        // maar elk create-doel wiens routePath-template dauGuid/pamGuid nodig heeft (Contact, Notice,
        // FunctionMember, DomainUser, Project, Category, ContactType, ObservationType, ...) kreeg dan
        // een routePath met het letterlijke woord "undefined" erin. PageData.create detecteert dat
        // (page-data.ts: .includes('undefined') ⇒ routePath = null) en page_goto logt dan alleen een
        // dev-warning en doet verder niets — vandaar dat de Add-knop op die tabs stil niets deed.
        // Huidige route-context meegeven lost dit op; voor routes zonder dauGuid/pamGuid in hun
        // template heeft dit geen effect (die keys worden simpelweg niet gebruikt).
        this.page_goto(this.PageCreate_name as TRoutePage, this._activatedRouteService?.routeKeys ?? {}, null, routeParams ?? null)
    }

    footerData_set(value: Partial<IFooterData>): void {
        this.__footerData_page$$.set(value);
    }

    headerData_set(value: Partial<IHeaderData>): void {
        this.__headerData_page$$.set(value);
    }

    navigate(urlFragments: any[], navExtras?: NavigationExtras) {

        this.__navigate(urlFragments, navExtras);
    }

    private __navigate(urlFragments: any[], navExtras?: NavigationExtras): void {
        this._router.navigate(urlFragments, navExtras).catch(err => {
            if (err?.name !== 'AbortError') throw err;
        });
    }

    page_goto(routePath?: string | null, routeActions?: RouteActionParam[] | null, routeParams?: {[key: string]: string} | null): void;
    page_goto(pageData: IPageData | null, routeActions?: RouteActionParam[] | null, routeParams?: {[key: string]: string} | null): void;
    page_goto(page: TRoutePage, routeKeys: IRouteKeyData, routeActions?: RouteActionParam[] | null, routeParams?: {[key: string]: string} | null): void;
    page_goto(
        value1: TRoutePage | IPageData | string | null | undefined, 
        value2: RouteActionParam[] | IRouteKeyData | null | undefined, 
        value3?: {[key: string]: string} | RouteActionParam[] | null | undefined, 
        routeParams?: {[key: string]: string} | null | undefined,
    ): void {
        let __routePath = '';
        let __pageData: IPageData | null = null;
        let __routeActions: RouteActionParam[] = [];
        let __routeParams: {[key: string]: string} = {};

        if (Object.values(routePage).includes(value1 as TRoutePage)) {
            const __pageName = value1 as TRoutePage;
            const __routeData = value2 as IRouteKeyData;
            const __routeStrings: { [key: string]: string } = 
                !__routeData 
                ? {} 
                : Object.fromEntries(
                    Object.entries(__routeData).filter(([key, value]) => (key == routeKeyData.dauGuid || key == routeKeyData.pamGuid) && value != null)
                ) as { [key: string]: string };
            __pageData = PageData.create(__pageName, __routeData) as IPageData;
            __routePath = __pageData.routePath ?? '';
            __routeActions = value3 as RouteActionParam[] ?? [];
            __routeParams = {...(__routeStrings ?? {}), ...(routeParams ?? {})};
        } else if (value1 && typeof(value1) === 'object') {
            __pageData = value1 as IPageData;
            __routePath = __pageData.routePath?.split('?')[0] ?? '';
            __routeActions = value2 as RouteActionParam[] ?? [];
            __routeParams = value3 as {[key: string]: string} ?? {};
        } else if (value1 && typeof(value1) === 'string') {
            __routePath = value1 as string;
            __routeActions = value2 as RouteActionParam[] ?? [];
            __routeParams = value3 as {[key: string]: string} ?? {};
        }

        if (!__routePath) {
            LogService.envDev.warn(`goto page [${__pageData?.routePage ?? '...'}]`, __pageData?.routePage ? 'routePath missing' : 'pageName missing');
            return;
        }

        // routeActionParam.keep hoeft de herkomst niet meer als query-param mee te sturen: de
        // navigatiestack houdt de aanroeper zelf vast.
        const _skipLocationChange = __routeActions.includes(routeActionParam.skipLocationChange);
        const _extras: Params = {
            queryParams: __routeParams,
            skipLocationChange: _skipLocationChange,
        }

        this.__navigate([__routePath], _extras);
    }

    // page_removeLoading() {
    //     this._loadingPageService.remove();
    // }

    pageCurrent_set(routePage: TRoutePage): void
    pageCurrent_set(pageData: IPageData): void
    pageCurrent_set(value: TRoutePage | IPageData): void {
        let _pageData: IPageData | null;

        if (Object.values(routePage).includes(value as TRoutePage)) {
            const _pageName = value as TRoutePage;
            // routeKeys is null voor sleutel-loze pagina's (bv. home/login/account/adminPanel) —
            // PageData.create/pageData_get komt daar prima mee overweg (die routePath-templates
            // refereren geen routeKeyData), dus geen early-return: anders blijft pageCurrent$$ op
            // die pagina's stilzwijgend null i.p.v. de juiste PageData te krijgen.
            const _routeKeyData = this._activatedRouteService?.routeKeys ?? null;

            _pageData = PageData.create(_pageName, _routeKeyData);
        } else if (value instanceof PageData) {
            _pageData = value;
        } else if (value && typeof value === 'object' && 'routePage' in value) {
            // Duck-typed: ook een plain object dat aan IPageData voldoet accepteren, niet alleen een
            // echte PageData-instantie — nodig om een al gezette pageCurrent$$ te corrigeren (bv.
            // Page vs Tab, zie PageComponent.ngOnInit) zonder PageData's eigen private constructor.
            _pageData = value as IPageData;
        } else {
            return;
        }

        this.__pageCurrent$$.set(_pageData);
    }

    pageData_get(routePage: TRoutePage, routeKeys?: IRouteKeyData): IPageData | null
    pageData_get(routePath: string, router: Router): PageData | null
    pageData_get(value?: TRoutePage | IRouteKeyData | string, value2?: IRouteKeyData | Router): IPageData | null {
        let __routePage: TRoutePage;
        let __routeKeys: IRouteKeyData;

       if (Object.values(routePage).includes(value as TRoutePage)) {
            const _activeRouteKeys = this._activatedRouteService?.routeKeys ?? {};
            const _inputRouteKeys = value2 as IRouteKeyData;

            __routeKeys = _inputRouteKeys ?? _activeRouteKeys;
            __routePage = value as TRoutePage;

            return PageData.create(__routePage, __routeKeys);
        } else if (typeof(value) === 'string') {
            const _routePath = value;
            const _router = value2 as Router;

            return PageData.create(_routePath, _router);
        } else {
            return null;
        }
    }

    pageReturn_goto(): void {
        const __pageReturn = this.PageReturn ?? this.PageHome;

        if (!__pageReturn) return;

        if (__pageReturn.routePage == null && __pageReturn.routePath) {
            this._router.navigateByUrl(__pageReturn.routePath);
        } else {
            this.page_goto(__pageReturn);
        }
    }

}
